import React, { useEffect, useState } from 'react';
import { supabase, Job, Application, Profile } from '../../lib/supabase';
import { ContractorProfileModal } from './ContractorProfileModal';
import { RecommendedContractors } from './RecommendedContractors';
import {
  X,
  MapPin,
  DollarSign,
  Calendar,
  Shield,
  CheckCircle2,
  Briefcase,
  Clock,
  User,
  MessageSquare,
  AlertCircle,
  Edit2,
  Trash2,
  Archive,
  Users,
  Search,
  TrendingUp
} from 'lucide-react';

interface JobDetailsModalProps {
  job: Job;
  onClose: () => void;
  onJobUpdated: () => void;
  onJobDeleted: () => void;
}

type ApplicationStatus = 'new' | 'contacted' | 'verified' | 'hired' | 'rejected';

export const JobDetailsModal: React.FC<JobDetailsModalProps> = ({
  job,
  onClose,
  onJobUpdated,
  onJobDeleted
}) => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [contractors, setContractors] = useState<Map<string, Profile>>(new Map());
  const [loading, setLoading] = useState(true);
  const [selectedContractor, setSelectedContractor] = useState<Profile | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | 'all'>('all');
  const [selectedApplications, setSelectedApplications] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadApplications();
  }, [job.id]);

  const loadApplications = async () => {
    try {
      const { data: appsData, error } = await supabase
        .from('applications')
        .select('*')
        .eq('job_id', job.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const apps = appsData || [];
      const contractorIds = [...new Set(apps.map(a => a.contractor_id))];

      if (contractorIds.length > 0) {
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('*')
          .in('id', contractorIds);

        const profileMap = new Map<string, Profile>();
        (profilesData || []).forEach(p => profileMap.set(p.id, p));
        setContractors(profileMap);
      }

      setApplications(apps);
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationStatus = async (appId: string, newStatus: ApplicationStatus) => {
    try {
      const { error } = await supabase
        .from('applications')
        .update({ status: newStatus })
        .eq('id', appId);

      if (error) throw error;
      await loadApplications();
    } catch (error) {
      console.error('Error updating application:', error);
    }
  };

  const bulkUpdateStatus = async (status: ApplicationStatus) => {
    if (selectedApplications.size === 0) return;

    try {
      const { error } = await supabase
        .from('applications')
        .update({ status })
        .in('id', Array.from(selectedApplications));

      if (error) throw error;
      setSelectedApplications(new Set());
      await loadApplications();
    } catch (error) {
      console.error('Error bulk updating:', error);
    }
  };

  const handleCloseJob = async () => {
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ status: 'closed' })
        .eq('id', job.id);

      if (error) throw error;
      onJobUpdated();
      onClose();
    } catch (error) {
      console.error('Error closing job:', error);
    }
  };

  const handleDeleteJob = async () => {
    try {
      const { error } = await supabase
        .from('jobs')
        .delete()
        .eq('id', job.id);

      if (error) throw error;
      onJobDeleted();
      onClose();
    } catch (error) {
      console.error('Error deleting job:', error);
    }
  };

  const toggleSelectApplication = (appId: string) => {
    const newSelected = new Set(selectedApplications);
    if (newSelected.has(appId)) {
      newSelected.delete(appId);
    } else {
      newSelected.add(appId);
    }
    setSelectedApplications(newSelected);
  };

  const selectAll = () => {
    if (selectedApplications.size === filteredApplications.length) {
      setSelectedApplications(new Set());
    } else {
      setSelectedApplications(new Set(filteredApplications.map(app => app.id)));
    }
  };

  const filteredApplications = applications.filter(app => {
    const contractor = contractors.get(app.contractor_id);
    const matchesSearch = !searchTerm ||
      contractor?.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contractor?.trade_type?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusCounts = {
    all: applications.length,
    new: applications.filter(a => a.status === 'new').length,
    contacted: applications.filter(a => a.status === 'contacted').length,
    verified: applications.filter(a => a.status === 'verified').length,
    hired: applications.filter(a => a.status === 'hired').length,
    rejected: applications.filter(a => a.status === 'rejected').length,
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'contacted':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'verified':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'hired':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'rejected':
        return 'bg-slate-100 text-slate-600 border-slate-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
        <div className="bg-white rounded-2xl shadow-xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col my-4">
          <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
            <div className="flex-1 min-w-0 pr-4">
              <h2 className="text-2xl font-bold text-slate-900 truncate">{job.title}</h2>
              <p className="text-sm text-slate-600 mt-1">{applications.length} applications</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-gradient-to-br from-slate-50 to-amber-50 rounded-xl p-6 border border-slate-200">
                    <h3 className="font-bold text-slate-900 mb-4 flex items-center">
                      <Briefcase className="w-5 h-5 mr-2 text-amber-600" />
                      Job Details
                    </h3>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center text-slate-700">
                        <MapPin className="w-4 h-4 mr-2 text-slate-400" />
                        <span className="text-sm">{job.location}</span>
                      </div>
                      <div className="flex items-center text-slate-700">
                        <DollarSign className="w-4 h-4 mr-2 text-slate-400" />
                        <span className="text-sm">${job.pay_rate}/{job.pay_type === 'hourly' ? 'hr' : job.pay_type}</span>
                      </div>
                      <div className="flex items-center text-slate-700">
                        <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                        <span className="text-sm">
                          {job.start_date ? new Date(job.start_date).toLocaleDateString() : 'TBD'}
                        </span>
                      </div>
                      <div className="flex items-center text-slate-700">
                        <Briefcase className="w-4 h-4 mr-2 text-slate-400" />
                        <span className="text-sm">{job.trade_type}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mb-4">
                      {job.insurance_required && (
                        <span className="flex items-center px-2.5 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-full border border-amber-200">
                          <Shield className="w-3 h-3 mr-1" />
                          Insurance Required
                        </span>
                      )}
                      {job.verification_required && (
                        <span className="flex items-center px-2.5 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full border border-green-200">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Verification Required
                        </span>
                      )}
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full border capitalize ${
                        job.status === 'open' ? 'bg-green-50 text-green-700 border-green-200' :
                        job.status === 'filled' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        'bg-slate-100 text-slate-700 border-slate-200'
                      }`}>
                        {job.status}
                      </span>
                    </div>

                    <div>
                      <h4 className="font-medium text-slate-900 mb-2">Description</h4>
                      <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">
                        {job.description}
                      </p>
                    </div>

                    {job.duration && (
                      <div className="mt-4">
                        <h4 className="font-medium text-slate-900 mb-2">Duration</h4>
                        <p className="text-slate-600 text-sm">{job.duration}</p>
                      </div>
                    )}
                  </div>

                  <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-slate-900 flex items-center">
                        <Users className="w-5 h-5 mr-2 text-amber-600" />
                        Applications ({filteredApplications.length})
                      </h3>
                      {selectedApplications.size > 0 && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-600">{selectedApplications.size} selected</span>
                          <select
                            onChange={(e) => bulkUpdateStatus(e.target.value as ApplicationStatus)}
                            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white"
                            defaultValue=""
                          >
                            <option value="" disabled>Bulk Action</option>
                            <option value="contacted">Mark as Contacted</option>
                            <option value="verified">Mark as Verified</option>
                            <option value="hired">Mark as Hired</option>
                            <option value="rejected">Mark as Rejected</option>
                          </select>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 mb-4">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search contractors..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-50 outline-none"
                        />
                      </div>
                      <div className="flex items-center gap-2 overflow-x-auto">
                        {(['all', 'new', 'contacted', 'verified', 'hired'] as const).map((status) => (
                          <button
                            key={status}
                            onClick={() => setStatusFilter(status)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                              statusFilter === status
                                ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-md'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            }`}
                          >
                            {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)} ({statusCounts[status]})
                          </button>
                        ))}
                      </div>
                    </div>

                    {loading ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
                      </div>
                    ) : filteredApplications.length === 0 ? (
                      <div className="text-center py-12">
                        <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-600">
                          {statusFilter !== 'all' ? `No ${statusFilter} applications` : 'No applications yet'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center px-4 py-2 bg-slate-50 rounded-lg">
                          <input
                            type="checkbox"
                            checked={selectedApplications.size === filteredApplications.length && filteredApplications.length > 0}
                            onChange={selectAll}
                            className="w-4 h-4 text-amber-600 border-slate-300 rounded focus:ring-amber-500"
                          />
                          <span className="ml-3 text-sm font-medium text-slate-700">Select All</span>
                        </div>
                        {filteredApplications.map((app) => {
                          const contractor = contractors.get(app.contractor_id);
                          return (
                            <div
                              key={app.id}
                              className={`border rounded-lg p-4 transition-all ${
                                selectedApplications.has(app.id)
                                  ? 'border-amber-300 bg-amber-50'
                                  : 'border-slate-200 hover:shadow-md'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <input
                                  type="checkbox"
                                  checked={selectedApplications.has(app.id)}
                                  onChange={() => toggleSelectApplication(app.id)}
                                  className="mt-1 w-4 h-4 text-amber-600 border-slate-300 rounded focus:ring-amber-500"
                                />
                                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                                  <User className="w-5 h-5 text-slate-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <button
                                          onClick={() => setSelectedContractor(contractor || null)}
                                          className="font-semibold text-slate-900 hover:text-amber-600 truncate text-left"
                                        >
                                          {contractor?.full_name || 'Unknown'}
                                        </button>
                                        {contractor?.verification_status === 'verified' && (
                                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                                        )}
                                      </div>
                                      <p className="text-xs text-slate-600 truncate">
                                        {contractor?.trade_type} • {contractor?.jobs_completed || 0} jobs • ⭐ {contractor?.rating.toFixed(1)}
                                      </p>
                                    </div>
                                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium border capitalize ${getStatusColor(app.status)}`}>
                                      {app.status}
                                    </span>
                                  </div>

                                  {app.message && (
                                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">{app.message}</p>
                                  )}

                                  {app.proposed_rate && (
                                    <div className="text-sm text-slate-600 mb-3">
                                      Proposed rate: <span className="font-semibold">${app.proposed_rate}</span>
                                    </div>
                                  )}

                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => updateApplicationStatus(app.id, 'contacted')}
                                      className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                                    >
                                      <MessageSquare className="w-3 h-3 inline mr-1" />
                                      Contact
                                    </button>
                                    <button
                                      onClick={() => updateApplicationStatus(app.id, 'verified')}
                                      className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                                    >
                                      <CheckCircle2 className="w-3 h-3 inline mr-1" />
                                      Verify
                                    </button>
                                    <button
                                      onClick={() => updateApplicationStatus(app.id, 'hired')}
                                      className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
                                    >
                                      Hire
                                    </button>
                                    <button
                                      onClick={() => setSelectedContractor(contractor || null)}
                                      className="px-3 py-1.5 text-xs font-medium text-slate-700 border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors"
                                    >
                                      View Profile
                                    </button>
                                  </div>

                                  <div className="flex items-center text-xs text-slate-500 mt-2">
                                    <Clock className="w-3 h-3 mr-1" />
                                    Applied {new Date(app.created_at).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-slate-50 to-amber-50 rounded-xl p-6 border border-slate-200">
                    <h3 className="font-bold text-slate-900 mb-4 flex items-center">
                      <TrendingUp className="w-5 h-5 mr-2 text-amber-600" />
                      Statistics
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-slate-600">Total Applications</span>
                          <span className="text-lg font-bold text-slate-900">{applications.length}</span>
                        </div>
                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-amber-500 to-amber-600" style={{ width: '100%' }}></div>
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-slate-600">Verified</span>
                          <span className="text-lg font-bold text-green-600">{statusCounts.verified}</span>
                        </div>
                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-green-500 to-green-600"
                            style={{ width: `${applications.length > 0 ? (statusCounts.verified / applications.length) * 100 : 0}%` }}
                          ></div>
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-slate-600">Hired</span>
                          <span className="text-lg font-bold text-amber-600">{statusCounts.hired}</span>
                        </div>
                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-amber-500 to-orange-600"
                            style={{ width: `${applications.length > 0 ? (statusCounts.hired / applications.length) * 100 : 0}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                    <h3 className="font-bold text-slate-900 mb-4">Job Actions</h3>
                    <div className="space-y-2">
                      <button className="w-full px-4 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-medium rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center">
                        <Edit2 className="w-4 h-4 mr-2" />
                        Edit Job
                      </button>

                      {job.status === 'open' && (
                        <button
                          onClick={handleCloseJob}
                          className="w-full px-4 py-2.5 bg-amber-50 hover:bg-amber-100 text-amber-700 font-medium rounded-lg transition-colors flex items-center justify-center border border-amber-200"
                        >
                          <Archive className="w-4 h-4 mr-2" />
                          Close Job
                        </button>
                      )}

                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="w-full px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-700 font-medium rounded-lg transition-colors flex items-center justify-center border border-red-200"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Job
                      </button>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-300 shadow-md">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0 shadow-md">
                        <TrendingUp className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-amber-900 mb-1">Job Insights</h4>
                        <p className="text-sm text-amber-700">
                          Your job is performing well! Average time to first application: 2.3 hours
                        </p>
                      </div>
                    </div>
                  </div>

                  {applications.length === 0 && job.status === 'open' && (
                    <RecommendedContractors
                      job={job}
                      onViewProfile={(contractorId) => {
                        const contractor = contractors.get(contractorId);
                        if (contractor) setSelectedContractor(contractor);
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Delete Job?</h3>
                <p className="text-sm text-slate-600">
                  This will permanently delete the job and all {applications.length} application(s). This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteJob}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
              >
                Delete Job
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedContractor && (
        <ContractorProfileModal
          contractor={selectedContractor}
          onClose={() => setSelectedContractor(null)}
        />
      )}
    </>
  );
};
